import { useEffect } from "react";
import type { DateOnly } from "./dateOnly";
import { stringToDateOnly } from "./dateOnly";
import type { CostCurrency, CostInvoiceStatus } from "../costApi";
import { costCurrencies } from "../costApi";
import { przezmorzeApi } from "../../../api/api";
import { parseAmount } from "./parseAmount";

export interface OCRValue<T> {
  value: T;
  bbox?: { x1: number; y1: number; x2: number; y2: number; page?: number };
}

export interface OCRResult {
  total?: OCRValue<number>;
  date?: OCRValue<DateOnly>;
  currency?: OCRValue<CostCurrency>;
  invoiceNumber?: OCRValue<string>;
  page?: { width: number; height: number };
  exchangeRate?: number;
  status?: CostInvoiceStatus;
}

export interface ProcessInvoiceDTO {
  total?: OCRValue<string>;
  date?: OCRValue<string>;
  invoiceNumber?: OCRValue<string>;
  currency?: OCRValue<string>;
  page: {
    width: number;
    height: number;
  };
}

export async function processInvoice(file: File, abortSignal?: AbortSignal) {
  const body = new FormData();
  body.append("file", file, file.name);
  const response = await przezmorzeApi.post<ProcessInvoiceDTO>(`/document-ai/process-invoice`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export function OCRService({ file, onDetect }: { file: File; onDetect: (result: OCRResult) => void }) {
  useEffect(() => {
    (async () => {
      if (!file) {
        return;
      }
      try {
        const response = await processInvoice(file);
        const result: OCRResult = {
          invoiceNumber: response.invoiceNumber,
          page: response.page,
        };
        if (response.total) {
          result.total = ocrAmount(response.total);
        }
        if (response.date) {
          result.date = ocrDateOnly(response.date);
        }
        if (response.currency) {
          const parsedCurrency = ocrCurrency(response.currency);
          if (parsedCurrency.value !== null) {
            result.currency = parsedCurrency as OCRValue<CostCurrency>;
          }
        }
        onDetect(result);
      } catch (e) {
        console.error("OCR error", e);
        onDetect({});
      }
    })();
  }, [file, onDetect]);
  return <></>;
}

export function ocrAmount(v: OCRValue<string>): OCRValue<number> {
  return {
    ...v,
    value: parseAmount(v.value),
  };
}

export function ocrCurrency(v: OCRValue<string>): OCRValue<CostCurrency | null> {
  const asCurrency = v.value as CostCurrency;
  return {
    ...v,
    value: costCurrencies.includes(asCurrency) ? asCurrency : null,
  };
}

export function ocrDateOnly(v: OCRValue<string>): OCRValue<DateOnly> {
  return {
    ...v,
    value: stringToDateOnly(v.value),
  };
}
